import React, { FC } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'

import {
  IData_Org_With_Profile,
} from '@kaidam/fc-shared/src/types/rbac'

const TermsAndConditions: FC<React.PropsWithChildren<{
  org: IData_Org_With_Profile,
  onOpenPrivacy: {
    (): void,
  }
  onClose: {
    (): void,
  }
}>> = ({
  org,
  onOpenPrivacy,
  onClose,
}) => {
  const {
    name,
    address,
    email,
  } = org.profile

  return (
    <Dialog maxWidth="lg" onClose={onClose} aria-labelledby="customized-dialog-title" open>
      <DialogTitle>
        Terms & Conditions
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="large"
          sx={{
            position: 'absolute',
            right: (theme) => theme.spacing(1),
            top: (theme) => theme.spacing(1),
            color: (theme) => theme.palette.grey[500],
          }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Introduction
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          These terms and conditions set out the basis on which we, The CHEESE Project CIC, agree to carry out home thermal imaging surveys and will apply to the contract that is made, at the time of your booking a survey with us, between you, as the person booking the survey, and us.
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Contents
        </Typography>
        <Typography component="div" variant="body1" gutterBottom sx={{ mb: 2 }}>
          <ol>
            <li><a href="#1">Payment for the survey</a></li>
            <li><a href="#2">Cancellation of your survey booking</a></li>
            <li><a href="#3">Covid precautions</a></li>
            <li><a href="#4">Preparation for the survey</a></li>
            <li><a href="#5">Previous energy use</a></li>
            <li><a href="#6">Gaining access to your home</a></li>
            <li><a href="#7">During the survey</a></li>
            <li><a href="#8">Training surveys</a></li>
            <li><a href="#9">Limitations of our survey service</a></li>
            <li><a href="#10">Follow ups after the survey</a></li>
            <li><a href="#11">Circumstances beyond our control</a></li>
            <li><a href="#12">Privacy</a></li>
          </ol>
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <a id="1"></a>
          1. Payment for the survey
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          Unless we have agreed to provide you with a free survey:
        </Typography>
        <Typography component="div" variant="body1" gutterBottom sx={{ mb: 2 }}>
          <ul>
            <li>you must pay us the full amount of the survey price (minus any eligible discounts) in advance at the time of booking the survey with us; and</li>
            <li>we will only treat the survey booking as having been made once we have received the full amount of the survey price from you.</li>
          </ul>
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          Our survey prices are set according to the number of rooms in the home being surveyed, because this determines how long the survey takes. See our pricing for various sizes of homes.
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <a id="2"></a>
          2. Cancellation of your survey booking
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          You can cancel your survey booking and receive a full refund of the survey price if you tell us within 14 days of making the booking that you would like to cancel. The only exception to this is if the date of your survey is less than 14 days after you booked it. In this situation, where you specifically confirm to us - after making the booking - that you wish to see the survey go ahead, you will lose the right to change your mind afterwards and cancel the booking (unless you give us at least 72 hours' notice - see next paragraph below).
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          Once the period of 14 days has passed from the date you booked the survey, you will still be able to cancel the survey, but you must tell us at least 72 hours ahead of the time we have arranged to carry out the survey that you wish to cancel. If you do not give us at least 72 hours' advance notice of cancellation, we will have the right to charge you a cancellation fee on the following basis:
        </Typography>
        <Typography component="div" variant="body1" gutterBottom sx={{ mb: 2 }}>
          <ul>
            <li>if you do not want to re-arrange to a different time, later in the same survey season (the period which runs from October to April), our cancellation fee will be £100. Where you have already paid us the survey price, we will refund the survey price to you, less the £100 cancellation fee;</li>
            <li>if, within 14 days after the date of your original booking, you agree with us to have a survey carried out at different time later in the same survey season, our cancellation fee will only be £50. You will need to pay us this £50 cancellation fee at the time of agreeing with us the re-arranged survey.</li>
          </ul>
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          The best way to contact us in order to tell us you wish to cancel your survey booking is to email us at surveys@cheeseproject.co.uk.
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          If you do not tell us in advance that you wish to cancel a survey booking but our surveyor is unable to carry out the survey, at the time we have arranged with you, either because sufficient preparations for the survey have not been made (see section 4 below) or because our surveyor cannot get the necessary access to your home (see section 6 below), we will have the right to treat this situation as being the same as one in which you cancel the booking on less than 72 hours' notice. This means that we will have the right to charge you a cancellation fee of either £100 or £50, depending on whether or not you agree with us to have a survey carried out at a different time later in the same survey season.
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <a id="3"></a>
          3. Covid precautions
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          To ensure the safety of our staff, we regret that we will have to postpone the survey if, on the day of the survey, anyone in your household is self isolating, awaiting a Covid test, is displaying any Covid symptoms, or has been in contact with a positive case in the last 10 days.
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          Please inform us if you require our surveyors to wear masks and have wipes to clean door handles etc. Since the doors and windows need to closed throughout the survey, our surveyors will do their best to keep a distance between you both.
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <a id="4"></a>
          4. Preparation for the survey
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          In order for us to conduct an efficient and successful survey, we need you to make some preparations around your home before the survey:
        </Typography>
        <Typography component="div" variant="body1" gutterBottom sx={{ mb: 2 }}>
          <ul>
            <li>We recommend that you switch on your heating for 24 hours before the survey to thoroughly warm up the fabric of the house (this is to create a difference of at least 8 degrees Celsius between the house and the external temperature). Please make sure that all radiators are turned on; however you might want to adjust the temperature of individual radiators/rooms so that rooms do not become uncomfortable, particularly at night. Please also note, if you feel that you're unable to keep your heating on for this length of time, contact us at: surveys@cheeseproject.co.uk so we can work out the best approach for you.</li>
            <li>Switch off/stop any wood burners, open fires, Agas/Rayburns, gas fires etc. two hours before the survey. This is important since we will reduce the pressure inside the house during the survey, and this might draw exhaust gases or smoke into the house</li>
            <li>For the same reason, block chimneys (with a pillow or cushion in a plastic bag, or a chimney balloon) and cover any open fireplaces to prevent soot from being drawn into rooms.</li>
            <li>Clear away any items that will make it harder to spot draughts and cold spots (e.g. on window sills, furniture in corners), and to make it safe for the surveyor to move around easily without tripping etc.</li>
            <li>Ensure there is a space for our thermal-imaging equipment, such as a kitchen table or worktop, and a nearby plug socket for charging.</li>
            <li>Half an hour before the survey:
              <ul>
                <li>Switch off the central heating.</li>
                <li>Open all curtains and blinds.</li>
              </ul>
            </li>
          </ul>
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          If you have not made sufficient preparations for our surveyor to carry out a survey safely at the time we have arranged with you for the survey to be carried out, we will have the right to treat this situation as being the same as one in which you cancel the survey at short notice. Please see section 2 above for more detail about the rules which apply to cancellations.
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <a id="5"></a>
          5. Previous energy use
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          It is very important for us to record data on energy use so that we can measure whether energy is being saved, and how much. We want to compare your energy use before the survey with it afterwards. To do this, we ask you to find out your total energy use for the year before your survey. One year after your survey, we will contact you again to ask you to calculate the same figures for comparison.
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          For the pre-survey use figures, please retrieve energy bills (electricity, gas and possibly solid fuel) for the year period before your survey, or as close as you have records for. For each type of energy, we need to know the total <a href="https://www.ovoenergy.com/guides/energy-guides/what-is-a-kwh-kw-and-kwh-explained" target="_blank">kilowatt hours (kWh) of energy</a> you have used. Please look through your bills and calculate the total kilowatt-hours figures to have them ready with the start and end dates of their period. For help with understanding gas and electricity bills from, there are useful guides from:
        </Typography>
        <Typography component="div" variant="body1" gutterBottom sx={{ mb: 2 }}>
          <ul>
            <li><a href="https://www.cse.org.uk/resource/home-energy-advice-leaflets/" target="_blank">Centre for Sustainable Energy</a></li>
            <li><a href="https://www.uswitch.com/gas-electricity/guides/energy-bills/" target="_blank">USwitch</a></li>
          </ul>
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          If you are not sure how to calculate these totals, then we can calculate them after the survey with you.
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          It is important that we collect energy use figures that are based on meter readings, rather than estimates made by the energy provider, so that our analyses are accurate. If you do not have meter readings available, then we can mark the figure as an estimate.
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <a id="6"></a>
          6. Gaining access to your home
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          You will need to make sure that someone is able to give us the access to your home that we need, at the time that we have arranged with you for the survey to be carried out.
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          If our surveyor is not able to have the necessary access to your home at the time we have arranged with you for the survey to be carried out, we will have the right to treat this situation as being the same as one in which you cancel the survey at short notice. Please see section 2 above for more detail about the rules which apply to cancellations.
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <a id="7"></a>
          7. During the survey
        </Typography>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          '& img': {
            width: '100%',
            maxWidth: '600px',
            minWidth: '200px',
          }
        }}>
          <img src="/img/misc/first-thermal-terms-image1.jpg" alt="A CHEESE blower door installed during a survey" />
          <Typography variant="caption" gutterBottom sx={{ mb: 2 }}>
            A CHEESE blower door installed during a survey.
          </Typography>
          <img src="/img/misc/first-thermal-terms-image2.jpg" alt="A CHEESE thermal imaging kit" />
          <Typography variant="caption" gutterBottom sx={{ mb: 2 }}>
            A CHEESE thermal imaging kit.
          </Typography>
          <img src="/img/misc/first-thermal-terms-image3.jpg" alt="Debriefing with the householder after a survey" />
          <Typography variant="caption" gutterBottom sx={{ mb: 2 }}>
            Debriefing with the householder after a survey
          </Typography>
        </Box>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          Please note:
        </Typography>
        <Typography component="div" variant="body1" gutterBottom sx={{ mb: 2 }}>
          <ul>
            <li>For a five-room house (living and bedrooms) a survey will take approximately two hours, including a de-briefing at the end. Larger houses will take longer, a flat somewhat less. See the survey information page for survey timings based on building size.</li>
            <li>A blower door, consisting of a large fan and airtight sheet, will be fitted to a suitable external door. This is used to reduce the pressure inside of the house slightly to induce draughts. You will not be able to use the door it is fitted to for between 1 and 1.5 hours and it will also prevent anyone else having access if, for example, it is a shared doorway. Please note that the fans we use are large and relatively noisy, but are essential to obtain good results from the thermal imaging.</li>
            <li>We encourage your participation and engagement throughout the survey, since it is led by your own knowledge of the building and because it is important that you see for yourself where heat is being lost.</li>
            <li>As the thermal survey is conducted we encourage you to watch the images on a tablet computer.</li>
            <li>During the survey a video with sound will be made, recording what is found together with interpretation comments by the surveyor and your own comments or questions. Still thermal photos are also recorded.</li>
            <li>Following the debriefing on the findings at the end of the survey you will be given a recording of the video and its sound commentary on a USB memory stick. This will also contain information from The Centre for Sustainable Energy (CSE) on how to deal with energy saving opportunities. <strong>Please make sure you have a device that can be used to view the contents, if not then please let us know before your survey.</strong> You will also receive a short summary of the findings, made on paper.</li>
          </ul>
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <a id="8"></a>
          8. Training surveys
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          Each survey season we will be training new surveyors who need to accompany experienced surveyors on some surveys to practice their skills and interpretation of images. We may contact you to ask if you are willing to accept a training survey. This will mean one or two more people attending the survey. The advantage to you is that you will also have the most expert surveyors carrying out your survey.
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <a id="9"></a>
          9. Limitations of our survey service
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          <strong>Please note:</strong> our expertise is in discovering how energy moves around your home through the interpretation of thermal images, and using that information to identify thermal faults in the fabric of the building. During your survey we will discuss potential ways in which problems can be remedied, but we cannot offer this as professional advice. You must always consult an expert to determine the best way forward.
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          The CHEESE Project CIC can not make any judgement or statement on the quality of work carried out by a contractor for a survey customer, or who may be responsible for the conditions which may be revealed during your survey. Customers are welcome to use their CHEESE survey media as they see fit, but should not make any edits to the survey recording, nor attribute any judgements to CHEESE outside of the factual observations of the thermal conditions discussed within the survey, in its full context.
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <a id="10"></a>
          10. Follow ups after the survey
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          <strong>One month after:</strong>
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          Approximately one month after the survey, we will ask you to feedback on the actions you have taken (or plan to take) as a result of the survey. We would also like to hear any thoughts or comments you have on the survey itself.
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          <strong>One year after:</strong>
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          We will contact you one year after the survey by telephone or email to find out how you are getting on. We would like to know:
        </Typography>
        <Typography component="div" variant="body1" gutterBottom sx={{ mb: 2 }}>
          <ul>
            <li><strong>Your actual (not estimated) energy use since the survey.</strong> This is to compare with the figures we recorded on the day of the survey. We would like you to calculate the total kilowatt hours of electricity and gas you have used that year, from your bills, as you did before the survey.</li>
            <li><strong>What actions over the year you have taken to reduce energy use</strong></li>
            <li>We would also like to know how much money you have saved as a consequence of saving energy, from your bills.</li>
            <li>Any other comments that you might have.</li>
          </ul>
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          As with the one-month feedback form, the one-year feedback form can be completed by you or by a surveyor on your behalf.
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          <strong>Subsequently:</strong>
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          We find that following a home thermal imaging survey, people may continue to take action for years afterwards, reviewing the video again and again as they gradually deal with the problems it reveals. We will always be glad to hear of your progress in retrofitting and energy/money saving.
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <a id="11"></a>
          11. Circumstances beyond our control
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          We will use all reasonable efforts to ensure that a surveyor is able to carry out the survey at the time we have arranged with you to have the survey carried out. However, exceptionally, circumstances beyond our reasonable control may mean that we are unable to carry out the survey at the time we have arranged with you to have the survey carried out. This includes (as two examples, but not an exhaustive list):
        </Typography>
        <Typography component="div" variant="body1" gutterBottom sx={{ mb: 2 }}>
          <ul>
            <li>circumstances in which the surveyor scheduled to carry out the survey is ill or otherwise unable, due to circumstances beyond their reasonable control, to carry out the survey at the time arranged with you</li>
            <li>circumstances in which government restrictions (for example, restrictions imposed due to Covid or severe weather warnings) stop us from carrying out surveys.</li>
          </ul>
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          Where any of these circumstances occur, we will not be treated as having broken our contract with you as a result of the failure to carry out the survey at the time arranged with you. However, we will try to tell you about our inability to carry out the survey ahead of the time arranged with you, so you are not left waiting at home for a surveyor to arrive without knowing what is going on. We will also give you the choice of either re-arranging the survey for a later date or cancelling the original booking and receiving a full refund from us of the survey price (where already paid to us).
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <a id="12"></a>
          12. Privacy
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          We be collecting from you and processing certain personal data. Our <a href="#" onClick={ onOpenPrivacy }>privacy notice</a> explains what personal data we collect and ways in which we will process it.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TermsAndConditions